import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { catalogApi, warehouseApi } from '../../services/api';
import type { Service, ServiceCategory, ServiceFeature, Warehouse, ItemType, PricingModel } from '../../types';
import DaySelector from '../../components/DaySelector';

interface ServiceFormProps {
  mode: 'create' | 'edit';
}

const ServiceForm: React.FC<ServiceFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [logisticsRules, setLogisticsRules] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    category: '',
    description: '',
    short_description: '',
    // NOVO: Tipo de item (produto ou serviço)
    item_type: 'product' as ItemType,
    // Campos de preços para produtos
    daily_price: '',
    weekly_price: '',
    monthly_price: '',
    // NOVO: Campos para serviços
    pricing_model: '' as PricingModel | '',
    base_rate: '',
    service_days: [] as number[],
    // Outros campos
    availability_status: 'available' as 'available' | 'unavailable' | 'maintenance' | 'rented',
    is_active: true,
    featured: false,
    technical_specs: {} as Record<string, any>,
    total_quantity: 1,
    track_individual_items: false,
    primary_warehouse: '',
    // Campos de preços para veículos
    default_km_included: '',
    default_hours_included: '',
    price_per_km: '',
    price_per_hour: '',
    price_driver_daily: '',
    price_helper_daily: '',
    // Campos específicos para veículos
    axles_count: '',
    vehicle_type: '',
    // Acrescimo para fins de semana e feriados
    has_weekend_surcharge: false,
    surcharge_type: 'percentage' as 'percentage' | 'fixed',
    surcharge_value: '',
    // Logística automática
    logistics_enabled: false,
    logistics_rule: null as string | null
  });

  const [loadingCodeSuggestion, setLoadingCodeSuggestion] = useState(false);

  const [features, setFeatures] = useState<Partial<ServiceFeature>[]>([]);
  const [newFeature, setNewFeature] = useState({ name: '', description: '', icon: '' });
  const [newTechnicalSpec, setNewTechnicalSpec] = useState({ key: '', value: '' });

  useEffect(() => {
    loadCategories();
    loadWarehouses();
    loadLogisticsRules();
    if (mode === 'edit' && id) {
      loadService();
    } else if (mode === 'create') {
      // Check for duplicate data in sessionStorage
      const duplicateData = sessionStorage.getItem('duplicateServiceData');
      if (duplicateData) {
        try {
          const service = JSON.parse(duplicateData);

          // Processar primary_warehouse: pode vir como objeto com id ou como ID direto
          let warehouseId = '';
          if (service.primary_warehouse) {
            const warehouse = service.primary_warehouse as any;
            if (typeof warehouse === 'object' && warehouse.id) {
              warehouseId = warehouse.id.toString();
            } else {
              warehouseId = warehouse.toString();
            }
          }

          setFormData({
            code: '', // Não duplicar código - será gerado novo
            name: service.name || '',
            category: service.category?.id?.toString() || '',
            description: service.description || '',
            short_description: service.short_description || '',
            // NOVO: Tipo de item e campos de serviço
            item_type: service.item_type || 'product',
            daily_price: service.daily_price?.toString() || '',
            weekly_price: service.weekly_price?.toString() || '',
            monthly_price: service.monthly_price?.toString() || '',
            pricing_model: service.pricing_model || '',
            base_rate: service.base_rate?.toString() || '',
            service_days: service.service_days || [],
            availability_status: service.availability_status || 'available',
            is_active: service.is_active !== undefined ? service.is_active : true,
            featured: service.featured || false,
            technical_specs: service.technical_specs || {},
            total_quantity: service.total_quantity || 1,
            track_individual_items: service.track_individual_items || false,
            primary_warehouse: warehouseId,
            default_km_included: service.default_km_included?.toString() || '',
            default_hours_included: service.default_hours_included?.toString() || '',
            price_per_km: service.price_per_km?.toString() || '',
            price_per_hour: service.price_per_hour?.toString() || '',
            price_driver_daily: service.price_driver_daily?.toString() || '',
            price_helper_daily: service.price_helper_daily?.toString() || '',
            axles_count: service.axles_count?.toString() || '',
            vehicle_type: service.vehicle_type || '',
            has_weekend_surcharge: service.has_weekend_surcharge || false,
            surcharge_type: service.surcharge_type || 'percentage',
            surcharge_value: service.surcharge_value?.toString() || '',
            logistics_enabled: service.logistics_enabled || false,
            logistics_rule: service.logistics_rule || null
          });
          if (service.category) {
            setSelectedCategory(service.category);
          }
          // Clear the sessionStorage after loading
          sessionStorage.removeItem('duplicateServiceData');
        } catch (err) {
          console.error('Error loading duplicate data:', err);
          sessionStorage.removeItem('duplicateServiceData');
        }
      }
    }
  }, [mode, id]);

  const loadCategories = async () => {
    try {
      const data = await catalogApi.getCategories({ is_active: true, ordering: 'name' });
      setCategories(data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadWarehouses = async () => {
    try {
      const data = await warehouseApi.getWarehouses({ is_active: true, ordering: 'name' });
      setWarehouses(data.results);
    } catch (err) {
      console.error('Error loading warehouses:', err);
    }
  };

  const loadLogisticsRules = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`;
      const response = await fetch(`${API_BASE_URL}/api/v1/logistics-rules/`);
      if (response.ok) {
        const data = await response.json();
        setLogisticsRules(data.results || data);
      }
    } catch (err) {
      console.error('Error loading logistics rules:', err);
    }
  };

  const loadService = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [service, serviceFeatures] = await Promise.all([
        catalogApi.getService(id),
        catalogApi.getServiceFeatures({ service: id, ordering: 'order,name' })
      ]);

      // Processar primary_warehouse: pode vir como objeto com id ou como ID direto
      let warehouseId = '';
      if (service.primary_warehouse) {
        const warehouse = service.primary_warehouse as any;
        if (typeof warehouse === 'object' && warehouse.id) {
          warehouseId = warehouse.id.toString();
        } else {
          warehouseId = warehouse.toString();
        }
      }

      setFormData({
        code: service.code || '',
        name: service.name,
        category: service.category.id.toString(),
        description: service.description,
        short_description: service.short_description || '',
        // NOVO: Tipo de item e campos de serviço
        item_type: service.item_type || 'product',
        daily_price: service.daily_price?.toString() || '',
        weekly_price: service.weekly_price?.toString() || '',
        monthly_price: service.monthly_price?.toString() || '',
        pricing_model: service.pricing_model || '',
        base_rate: service.base_rate?.toString() || '',
        service_days: service.service_days || [],
        availability_status: service.availability_status,
        is_active: service.is_active,
        featured: service.featured,
        technical_specs: service.technical_specs || {},
        total_quantity: service.total_quantity || 1,
        track_individual_items: service.track_individual_items || false,
        primary_warehouse: warehouseId,
        default_km_included: service.default_km_included?.toString() || '',
        default_hours_included: service.default_hours_included?.toString() || '',
        price_per_km: service.price_per_km?.toString() || '',
        price_per_hour: service.price_per_hour?.toString() || '',
        price_driver_daily: service.price_driver_daily?.toString() || '',
        price_helper_daily: service.price_helper_daily?.toString() || '',
        axles_count: service.axles_count?.toString() || '',
        vehicle_type: service.vehicle_type || '',
        has_weekend_surcharge: service.has_weekend_surcharge || false,
        surcharge_type: service.surcharge_type || 'percentage',
        surcharge_value: service.surcharge_value?.toString() || '',
        logistics_enabled: service.logistics_enabled || false,
        logistics_rule: service.logistics_rule || null
      });

      setSelectedCategory(service.category);
      setFeatures(serviceFeatures);
    } catch (err) {
      setError('Erro ao carregar equipamento');
      console.error('Error loading service:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCodeSuggestion = async (name: string) => {
    if (!name.trim() || mode === 'edit') return;

    try {
      setLoadingCodeSuggestion(true);
      const response = await fetch(`http://localhost:8000/api/v1/catalog/services/generate_code/?name=${encodeURIComponent(name)}`);

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, code: data.suggested_code }));
      }
    } catch (err) {
      console.error('Error fetching code suggestion:', err);
    } finally {
      setLoadingCodeSuggestion(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      // Tratar logistics_rule como string (UUID) ou null
      if (name === 'logistics_rule') {
        setFormData(prev => ({ ...prev, [name]: value || null }));
        return;
      }

      setFormData(prev => ({ ...prev, [name]: value }));

      // Buscar sugestão de código quando o nome mudar (apenas no modo criar)
      if (name === 'name' && mode === 'create' && value.trim().length >= 3) {
        fetchCodeSuggestion(value);
      }

      // NOVO: Se mudar tipo de item para "service", ajustar inventário
      if (name === 'item_type') {
        if (value === 'service') {
          setFormData(prev => ({
            ...prev,
            item_type: value as typeof prev.item_type,
            total_quantity: 0,
            track_individual_items: false,
            // Limpar preços de produtos se necessário
            daily_price: '',
            weekly_price: '',
            monthly_price: ''
          }));
        } else if (value === 'product') {
          setFormData(prev => ({
            ...prev,
            item_type: value as typeof prev.item_type,
            total_quantity: 1,
            // Limpar campos de serviço
            pricing_model: '',
            base_rate: '',
            service_days: []
          }));
        }
      }

      // If category changed, update selectedCategory
      if (name === 'category') {
        const category = categories.find(c => c.id.toString() === value);
        setSelectedCategory(category || null);
        console.log('Categoria selecionada:', category);
        console.log('Campos extras:', category?.extra_fields_schema);

        // Se for categoria de veículo, forçar configurações
        if (category?.is_vehicle_category) {
          setFormData(prev => ({
            ...prev,
            category: value,
            total_quantity: 1,
            track_individual_items: true
          }));
        }
      }
    }
  };

  const handleTechnicalSpecChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      technical_specs: {
        ...prev.technical_specs,
        [key]: value
      }
    }));
  };

  const addTechnicalSpec = () => {
    if (newTechnicalSpec.key.trim() && newTechnicalSpec.value.trim()) {
      handleTechnicalSpecChange(newTechnicalSpec.key.trim(), newTechnicalSpec.value.trim());
      setNewTechnicalSpec({ key: '', value: '' });
    }
  };

  const removeTechnicalSpec = (key: string) => {
    setFormData(prev => {
      const newSpecs = { ...prev.technical_specs };
      delete newSpecs[key];
      return { ...prev, technical_specs: newSpecs };
    });
  };

  const addFeature = () => {
    if (newFeature.name.trim()) {
      setFeatures(prev => [...prev, { ...newFeature, order: prev.length }]);
      setNewFeature({ name: '', description: '', icon: '' });
    }
  };

  const removeFeature = (index: number) => {
    setFeatures(prev => prev.filter((_, i) => i !== index));
  };

  // Render dynamic extra fields based on category schema
  const renderExtraField = (field: any) => {
    const fieldValue = formData.technical_specs[field.name] || '';

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={fieldValue}
            onChange={(e) => handleTechnicalSpecChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={fieldValue}
            onChange={(e) => handleTechnicalSpecChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={fieldValue}
            onChange={(e) => handleTechnicalSpecChange(field.name, e.target.value)}
            required={field.required}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        );

      case 'boolean':
        return (
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={fieldValue === 'true' || fieldValue === true}
              onChange={(e) => handleTechnicalSpecChange(field.name, e.target.checked.toString())}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-xs text-gray-700">Sim</span>
          </label>
        );

      case 'select':
        return (
          <select
            value={fieldValue}
            onChange={(e) => handleTechnicalSpecChange(field.name, e.target.value)}
            required={field.required}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="">Selecione...</option>
            {field.options?.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.code.trim() || !formData.category || !formData.description.trim()) {
      setError('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const serviceData = {
        ...formData,
        code: formData.code.trim(),
        category: parseInt(formData.category),
        // NOVO: Campos de tipo de item
        item_type: formData.item_type,
        pricing_model: formData.pricing_model || null,
        base_rate: formData.base_rate ? parseFloat(formData.base_rate) : null,
        service_days: formData.service_days.length > 0 ? formData.service_days : null,
        // Campos de preço para produtos
        daily_price: formData.daily_price ? parseFloat(formData.daily_price) : null,
        weekly_price: formData.weekly_price ? parseFloat(formData.weekly_price) : null,
        monthly_price: formData.monthly_price ? parseFloat(formData.monthly_price) : null,
        total_quantity: parseInt(formData.total_quantity.toString()) || 1,
        primary_warehouse: formData.primary_warehouse || null,
        // Converter campos de pricing de veículos
        default_km_included: formData.default_km_included ? parseInt(formData.default_km_included) : null,
        default_hours_included: formData.default_hours_included ? parseInt(formData.default_hours_included) : null,
        price_per_km: formData.price_per_km ? parseFloat(formData.price_per_km) : null,
        price_per_hour: formData.price_per_hour ? parseFloat(formData.price_per_hour) : null,
        price_driver_daily: formData.price_driver_daily ? parseFloat(formData.price_driver_daily) : null,
        price_helper_daily: formData.price_helper_daily ? parseFloat(formData.price_helper_daily) : null,
        axles_count: formData.axles_count ? parseInt(formData.axles_count) : null,
        vehicle_type: formData.vehicle_type || null,
        // Surcharge para fins de semana e feriados
        has_weekend_surcharge: formData.has_weekend_surcharge,
        surcharge_type: formData.surcharge_type,
        surcharge_value: formData.surcharge_value ? parseFloat(formData.surcharge_value) : 0,
        // Logística automática
        logistics_enabled: formData.logistics_enabled,
        logistics_rule: formData.logistics_rule,
      };

      let savedService: Service;
      
      if (mode === 'create') {
        savedService = await catalogApi.createService(serviceData as any);
      } else {
        savedService = await catalogApi.updateService(id!, serviceData as any);
      }

      // Salvar features
      if (mode === 'create') {
        // Para novo equipamento, criar todas as features
        for (let i = 0; i < features.length; i++) {
          const feature = features[i];
          await catalogApi.createServiceFeature({
            ...feature,
            service: savedService.id,
            order: i
          } as any);
        }
      }

      navigate('/catalog');
    } catch (err) {
      setError('Erro ao salvar equipamento');
      console.error('Error saving service:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && mode === 'edit') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-2 py-1 border-b border-gray-200">
          <h2 className="text-sm font-bold text-gray-800">
            {mode === 'create' ? 'Novo Equipamento' : 'Editar Equipamento'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-2 space-y-1">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-2">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Código *
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  disabled={mode === 'edit'}
                  className={`w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    mode === 'edit' ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder={mode === 'create' ? 'Ex: LMP-001' : ''}
                  required
                />
                {loadingCodeSuggestion && mode === 'create' && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {mode === 'edit'
                  ? 'Código não pode ser alterado'
                  : 'Sugerido automaticamente'}
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Equipamento/Serviço *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Categoria *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Selecione uma categoria</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Depósito Principal
              </label>
              <select
                name="primary_warehouse"
                value={formData.primary_warehouse}
                onChange={handleInputChange}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione um depósito</option>
                {warehouses.map(warehouse => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name} ({warehouse.code})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-0.5">
                Depósito onde este equipamento está armazenado
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              Descrição Curta
            </label>
            <input
              type="text"
              name="short_description"
              value={formData.short_description}
              onChange={handleInputChange}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              maxLength={300}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              Descrição Completa *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={2}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* NOVO: Tipo de Item (Produto ou Serviço) */}
          <div className="border rounded-md p-3 bg-indigo-50 border-indigo-300">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Tipo de Item
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <label className="flex items-center p-3 border rounded-md cursor-pointer transition-all hover:border-indigo-500"
                     style={{
                       borderColor: formData.item_type === 'product' ? '#6366f1' : '#e0e0e0',
                       backgroundColor: formData.item_type === 'product' ? '#eef2ff' : '#fff'
                     }}>
                <input
                  type="radio"
                  name="item_type"
                  value="product"
                  checked={formData.item_type === 'product'}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm">
                  <strong>Produto/Equipamento</strong>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Item físico com estoque e preços por período (dia/semana/mês)
                  </p>
                </span>
              </label>

              <label className="flex items-center p-3 border rounded-md cursor-pointer transition-all hover:border-indigo-500"
                     style={{
                       borderColor: formData.item_type === 'service' ? '#6366f1' : '#e0e0e0',
                       backgroundColor: formData.item_type === 'service' ? '#eef2ff' : '#fff'
                     }}>
                <input
                  type="radio"
                  name="item_type"
                  value="service"
                  checked={formData.item_type === 'service'}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm">
                  <strong>Serviço</strong>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Serviço sem estoque físico, com precificação por execução
                  </p>
                </span>
              </label>
            </div>
          </div>

          {/* Preços para PRODUTOS */}
          {formData.item_type === 'product' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Preço Diário (R$)
              </label>
              <input
                type="number"
                name="daily_price"
                value={formData.daily_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Preço Semanal (R$)
              </label>
              <input
                type="number"
                name="weekly_price"
                value={formData.weekly_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Preço Mensal (R$)
              </label>
              <input
                type="number"
                name="monthly_price"
                value={formData.monthly_price}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          )}

          {/* NOVO: Preços para SERVIÇOS */}
          {formData.item_type === 'service' && (
            <div className="border rounded-md p-3 bg-green-50 border-green-300">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                Configuração de Serviço
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Modelo de Precificação */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Modelo de Precificação *
                  </label>
                  <select
                    name="pricing_model"
                    value={formData.pricing_model}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="fixed">Preço Fixo</option>
                    <option value="hourly">Por Hora</option>
                    <option value="per_person">Por Pessoa</option>
                    <option value="recurring">Recorrente com Frequência</option>
                    <option value="per_unit">Por Unidade Atendida</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Como o serviço será cobrado
                  </p>
                </div>

                {/* Taxa Base */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Taxa Base (R$) *
                  </label>
                  <input
                    type="number"
                    name="base_rate"
                    value={formData.base_rate}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 122.15"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-0.5">
                    Valor base por execução
                  </p>
                </div>
              </div>

              {/* NOVO: Dias da Semana para serviços recorrentes */}
              {formData.pricing_model === 'recurring' && (
                <div className="mt-3 p-3 bg-white border border-green-200 rounded-md">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Dias da Semana (Padrão) *
                  </label>
                  <DaySelector
                    value={formData.service_days}
                    onChange={(days) => setFormData(prev => ({ ...prev, service_days: days }))}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Selecione os dias da semana em que o serviço será executado por padrão.
                    Isso pode ser ajustado individualmente em cada proposta.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Inventory Management - Somente para PRODUTOS */}
          {formData.item_type === 'product' ? (
            <div className={`border rounded-md p-2 ${selectedCategory?.is_vehicle_category ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className="text-xs font-semibold text-gray-800 mb-1">
                Controle de Inventário
                {selectedCategory?.is_vehicle_category && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-blue-600 text-white rounded">VEÍCULO</span>
                )}
              </h3>

              {selectedCategory?.is_vehicle_category && (
                <div className="mb-2 p-2 bg-blue-100 border border-blue-300 rounded text-xs text-blue-900">
                  <strong>Modo Veículo:</strong> Cada veículo é um equipamento único com quantidade fixa de 1 unidade e rastreamento QR automático.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Quantidade Total *
                  </label>
                  <input
                    type="number"
                    name="total_quantity"
                    value={formData.total_quantity}
                    onChange={handleInputChange}
                    min="1"
                    disabled={selectedCategory?.is_vehicle_category}
                    className={`w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${selectedCategory?.is_vehicle_category ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-0.5">
                    {selectedCategory?.is_vehicle_category
                      ? 'Fixo em 1 para veículos'
                      : 'Quantidade máxima disponível para locação'}
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">
                    Configurações
                  </label>
                  <div className="pt-1">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="track_individual_items"
                        checked={formData.track_individual_items}
                        onChange={handleInputChange}
                        disabled={selectedCategory?.is_vehicle_category}
                        className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${selectedCategory?.is_vehicle_category ? 'cursor-not-allowed' : ''}`}
                      />
                      <span className="ml-2 text-xs text-gray-700">
                        Rastreamento Individual com QR Code
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5 ml-6">
                      Controle individual de cada item
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Mensagem informativa para SERVIÇOS */
            <div className="border rounded-md p-3 bg-gray-50 border-gray-300">
              <h3 className="text-xs font-semibold text-gray-800 mb-1">
                Controle de Inventário
              </h3>
              <div className="p-3 bg-gray-100 border border-gray-300 rounded text-xs text-gray-700">
                <p className="flex items-center">
                  <span className="text-lg mr-2">ℹ️</span>
                  <strong>Não aplicável para serviços.</strong>
                </p>
                <p className="mt-1 ml-7">
                  Serviços não possuem estoque físico ou controle de quantidade. O controle é feito pela capacidade de execução da equipe.
                </p>
              </div>
            </div>
          )}

          {/* Configuração de Preços para Veículos */}
          {selectedCategory?.is_vehicle_category && (
            <div className="border rounded-md p-3 bg-green-50 border-green-300">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                ⚙️ Configuração de Preços (Veículos)
              </h3>

              <div className="mb-2 p-2 bg-white border border-green-200 rounded text-xs text-gray-700">
                <strong>Info:</strong> Configure os valores padrão e adicionais para este veículo. Esses valores serão usados como base nas propostas.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* KM Incluídos */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    KM Incluídos (diária base)
                  </label>
                  <input
                    type="number"
                    name="default_km_included"
                    value={formData.default_km_included}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="Ex: 100"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">Km incluídos no preço da diária</p>
                </div>

                {/* Horas Incluídas */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Horas Incluídas (diária base)
                  </label>
                  <input
                    type="number"
                    name="default_hours_included"
                    value={formData.default_hours_included}
                    onChange={handleInputChange}
                    min="0"
                    max="24"
                    placeholder="Ex: 8"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">Horas incluídas no preço da diária</p>
                </div>

                {/* Preço por KM */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Preço por KM Adicional
                  </label>
                  <input
                    type="number"
                    name="price_per_km"
                    value={formData.price_per_km}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 2.50"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">R$ por km excedente</p>
                </div>

                {/* Preço por Hora */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Preço por Hora Adicional
                  </label>
                  <input
                    type="number"
                    name="price_per_hour"
                    value={formData.price_per_hour}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 15.00"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">R$ por hora excedente</p>
                </div>

                {/* Preço Motorista */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Preço Motorista (diária)
                  </label>
                  <input
                    type="number"
                    name="price_driver_daily"
                    value={formData.price_driver_daily}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 300.00"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">R$ do motorista por dia</p>
                </div>

                {/* Preço Ajudante */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Preço Ajudante (diária)
                  </label>
                  <input
                    type="number"
                    name="price_helper_daily"
                    value={formData.price_helper_daily}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="Ex: 150.00"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">R$ do ajudante por dia</p>
                </div>

                {/* Número de Eixos (para veículos) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Número de Eixos
                  </label>
                  <input
                    type="number"
                    name="axles_count"
                    value={formData.axles_count}
                    onChange={handleInputChange}
                    min="0"
                    max="9"
                    placeholder="Ex: 2"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">Quantidade de eixos do veículo</p>
                </div>

                {/* Tipo de Veículo (para cálculo de pedágios) */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Tipo de Veículo (TollGuru)
                  </label>
                  <select
                    name="vehicle_type"
                    value={formData.vehicle_type}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Selecione o tipo</option>
                    <option value="2AxlesAuto">Carro/SUV/Pickup (2 eixos)</option>
                    <option value="2AxlesTruck">Caminhonete (2 eixos)</option>
                    <option value="3AxlesTruck">Caminhão Truck (3 eixos)</option>
                    <option value="4AxlesTruck">Caminhão Toco (4 eixos)</option>
                    <option value="5AxlesTruck">Caminhão Bitruck (5 eixos)</option>
                    <option value="6AxlesTruck">Carreta (6 eixos)</option>
                    <option value="7AxlesTruck">Carreta (7 eixos)</option>
                    <option value="8AxlesTruck">Carreta (8 eixos)</option>
                    <option value="9AxlesTruck">Carreta (9 eixos)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-0.5">Tipo para cálculo de pedágios</p>
                </div>
              </div>
            </div>
          )}

          {/* Acrescimo para Fins de Semana e Feriados */}
          <div className="border rounded-md p-3 bg-orange-50 border-orange-300">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              💰 Acréscimo para Fins de Semana e Feriados
            </h3>

            <div className="mb-2 p-2 bg-white border border-orange-200 rounded text-xs text-gray-700">
              <strong>Info:</strong> Configure acréscimos automáticos quando o serviço for executado em sábados, domingos ou feriados. Aplicável apenas para serviços recorrentes.
            </div>

            {/* Habilitar Acrescimo */}
            <div className="mb-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="has_weekend_surcharge"
                  checked={formData.has_weekend_surcharge}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Aplicar acréscimo em fins de semana e feriados
                </span>
              </label>
            </div>

            {formData.has_weekend_surcharge && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-4 border-l-2 border-orange-300">
                {/* Tipo de Acrescimo */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Tipo de Acréscimo
                  </label>
                  <select
                    name="surcharge_type"
                    value={formData.surcharge_type}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="percentage">Percentual (%)</option>
                    <option value="fixed">Valor Fixo (R$)</option>
                  </select>
                </div>

                {/* Valor do Acrescimo */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Valor do Acréscimo
                  </label>
                  <input
                    type="number"
                    name="surcharge_value"
                    value={formData.surcharge_value}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder={formData.surcharge_type === 'percentage' ? 'Ex: 20' : 'Ex: 50.00'}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formData.surcharge_type === 'percentage'
                      ? 'Percentual sobre o preço base (ex: 20 = 20%)'
                      : 'Valor adicional em reais (ex: 50.00)'}
                  </p>
                </div>
              </div>
            )}

            {formData.has_weekend_surcharge && formData.surcharge_value && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                <strong>Exemplo:</strong> Se o preço base é R$ 100,00, o preço em dias especiais será:
                {formData.surcharge_type === 'percentage' ? (
                  <span className="ml-1">
                    R$ {(100 * (1 + parseFloat(formData.surcharge_value || '0') / 100)).toFixed(2)}
                    ({formData.surcharge_value}% de acréscimo)
                  </span>
                ) : (
                  <span className="ml-1">
                    R$ {(100 + parseFloat(formData.surcharge_value || '0')).toFixed(2)}
                    (+R$ {parseFloat(formData.surcharge_value || '0').toFixed(2)})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Logística Automática */}
          <div className="border rounded-md p-3 bg-blue-50 border-blue-300">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              🚚 Logística Automática (3 Clicks e Tudo Pronto!)
            </h3>

            <div className="mb-2 p-2 bg-white border border-blue-200 rounded text-xs text-gray-700">
              <strong>Info:</strong> Quando habilitado, a regra de logística será aplicada AUTOMATICAMENTE quando este serviço for adicionado a uma proposta. Sem configuração manual!
            </div>

            {/* Habilitar Logística */}
            <div className="mb-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="logistics_enabled"
                  checked={formData.logistics_enabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">
                  Habilitar logística automática para este serviço
                </span>
              </label>
            </div>

            {formData.logistics_enabled && (
              <div className="pl-4 border-l-2 border-blue-300">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Regra de Logística *
                  </label>
                  <select
                    name="logistics_rule"
                    value={formData.logistics_rule || ''}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required={formData.logistics_enabled}
                  >
                    <option value="">Selecione uma regra de logística</option>
                    {logisticsRules.map(rule => (
                      <option key={rule.id} value={rule.id}>
                        {rule.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Escolha a regra de logística que será aplicada automaticamente
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Status de Disponibilidade
              </label>
              <select
                name="availability_status"
                value={formData.availability_status}
                onChange={handleInputChange}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="available">Disponível</option>
                <option value="unavailable">Indisponível</option>
                <option value="maintenance">Em Manutenção</option>
                <option value="rented">Alugado</option>
              </select>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-xs text-gray-700">Ativo</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-xs text-gray-700">Destacado</span>
              </label>
            </div>
          </div>

          {/* Dynamic Extra Fields - Based on Category Configuration */}
          {(() => {
            console.log('=== RENDER CHECK ===');
            console.log('selectedCategory:', selectedCategory);
            console.log('extra_fields_schema:', selectedCategory?.extra_fields_schema);
            console.log('fields:', selectedCategory?.extra_fields_schema?.fields);
            console.log('fields.length:', selectedCategory?.extra_fields_schema?.fields?.length);
            return null;
          })()}

          {selectedCategory?.extra_fields_schema?.fields && selectedCategory.extra_fields_schema.fields.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-md p-2">
              <h3 className="text-xs font-semibold text-purple-900 mb-2">
                Campos Específicos - {selectedCategory.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {selectedCategory.extra_fields_schema.fields.map((field: any) => (
                  <div key={field.name}>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {renderExtraField(field)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Especificações Técnicas Adicionais */}
          <div>
            <h3 className="text-xs font-semibold text-gray-800 mb-1">Especificações Técnicas</h3>

            {/* Add new technical spec */}
            <div className="bg-gray-50 p-2 rounded-md mb-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
                <input
                  type="text"
                  placeholder="Nome da especificação (ex: Largura)"
                  value={newTechnicalSpec.key}
                  onChange={(e) => setNewTechnicalSpec(prev => ({ ...prev, key: e.target.value }))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Valor (ex: 80cm)"
                  value={newTechnicalSpec.value}
                  onChange={(e) => setNewTechnicalSpec(prev => ({ ...prev, value: e.target.value }))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={addTechnicalSpec}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Adicionar
                </button>
              </div>
            </div>

            <div className="space-y-1">
              {Object.entries(formData.technical_specs).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between bg-white border border-gray-200 rounded-md p-1.5">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={key}
                      disabled
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                    />
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleTechnicalSpecChange(key, e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTechnicalSpec(key)}
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 ml-2"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-xs font-semibold text-gray-800 mb-1">Características</h3>
            
            {/* Add new feature */}
            <div className="bg-gray-50 p-2 rounded-md mb-1">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
                <input
                  type="text"
                  placeholder="Nome da característica"
                  value={newFeature.name}
                  onChange={(e) => setNewFeature(prev => ({ ...prev, name: e.target.value }))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Descrição"
                  value={newFeature.description}
                  onChange={(e) => setNewFeature(prev => ({ ...prev, description: e.target.value }))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="Ícone (opcional)"
                  value={newFeature.icon}
                  onChange={(e) => setNewFeature(prev => ({ ...prev, icon: e.target.value }))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={addFeature}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  Adicionar
                </button>
              </div>
            </div>

            {/* Features list */}
            <div className="space-y-1">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center justify-between bg-white border border-gray-200 rounded-md p-1.5">
                  <div className="flex-1">
                    <span className="font-medium text-xs text-gray-800">{feature.name}</span>
                    {feature.description && (
                      <span className="text-xs text-gray-600 ml-2">- {feature.description}</span>
                    )}
                    {feature.icon && (
                      <span className="text-xs text-blue-600 ml-2">({feature.icon})</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="px-2 py-1 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/catalog')}
              className="px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-1 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : mode === 'create' ? 'Criar Equipamento' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceForm;